import './SuggestionService.scss';

import { Accordion, Card, Container } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import AppButton from '../../../commons/buttons/AppButton';
import AppContainer from '../../../commons/containers/AppContainer';
import AppLoading from '../../../commons/AppLoading';
import CategoryAPI from '../CategoryAPI';
import CompanyAPI from '../../company/CompanyAPI';
import EmptyState from '../../../commons/EmptyState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HallAppToast from '../../../commons/HallAppToast';
import IntlCurrencyInput from 'react-intl-currency-input';
import MenuTitle from '../../../commons/menu/MenuTitle';
import ReportHeader from '../../report-full/components/ReportHeader';
import { displayName } from '../../../commons/util/HallAppUtil';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';

const currencyConfig = {
  locale: 'pt-BR',
  formats: {
    number: {
      BRL: {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

const categoryAPI = new CategoryAPI();
const companyAPI = new CompanyAPI();

function SuggestionService() {
  const [selectedServices, setSelectedServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const getAllSuggestions = async () => {
    try {
      setIsLoading(true);
      const response = await categoryAPI.getAllSuggestion();
      setSuggestions(response.data || []);
    } catch (_) {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllEmployees = async () => {
    try {
      const response = await companyAPI.getAllEmployee(companyAPI.getCompanyManagment().id);
      setEmployees(response.data || []);
    } catch (_) {
      setEmployees([]);
      HallAppToast.genericError();
    }
  };

  useEffect(() => {
    getAllSuggestions();
    getAllEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleService = (categoryId, serviceId) => {
    setSelectedServices((prev) => {
      const existingCategory = prev.find((c) => c.id === categoryId);

      if (existingCategory) {
        const alreadyAdded = existingCategory.services.find((s) => s.id === serviceId);
        if (alreadyAdded) {
          const updatedServices = existingCategory.services.filter((s) => s.id !== serviceId);
          if (updatedServices.length === 0) {
            return prev.filter((c) => c.id !== categoryId);
          }
          return prev.map((c) =>
            c.id === categoryId ? { ...c, services: updatedServices } : c
          );
        } else {
          return prev.map((c) =>
            c.id === categoryId ? { ...c, services: [...c.services, { id: serviceId }] } : c
          );
        }
      } else {
        return [...prev, { id: categoryId, services: [{ id: serviceId }] }];
      }
    });
  };

  const isSelected = (categoryId, serviceId) =>
    selectedServices.some((c) => c.id === categoryId && c.services.some((s) => s.id === serviceId));

  const saveAll = async () => {
    // Validação básica
    const hasInvalidService = selectedServices.some((cat) =>
      cat.services.some((s) => !s.duration || !s.value || s.value <= 0)
    );

    if (hasInvalidService) {
      HallAppToast.error('Preencha duração e valor para todos os serviços selecionados.');
      return;
    }

    try {
      setIsSaving(true);
      await categoryAPI.createBySuggestion(companyAPI.getCompanyManagment().id, selectedServices);
      HallAppToast.success('Serviços adicionados com sucesso!');
      setSelectedServices([]);
      getAllSuggestions();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsSaving(false);
    }
  };

  const getSelectedCount = (categoryId) => {
    const found = selectedServices.find((c) => c.id === categoryId);
    return found ? found.services.length : 0;
  };

  const getTotalSelectedCount = () => {
    return selectedServices.reduce((acc, cat) => acc + cat.services.length, 0);
  };

  const getEmployeeSelection = (categoryId, serviceId, employeeId) => {
    const cat = selectedServices.find((c) => c.id === categoryId);
    const service = cat?.services.find((s) => s.id === serviceId);
    return service?.employees?.find((e) => e.id === employeeId);
  };

  const toggleEmployeeFlag = (categoryId, serviceId, employeeId, active) => {
    setSelectedServices((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              services: cat.services.map((service) =>
                service.id === serviceId
                  ? {
                      ...service,
                      employees: updateOrInsert(service.employees || [], employeeId, {
                        active,
                        commission: active ? service.employees?.find((e) => e.id === employeeId)?.commission || 0 : null,
                      }),
                    }
                  : service
              ),
            }
          : cat
      )
    );
  };

  const updateEmployeeCommission = (categoryId, serviceId, employeeId, commission) => {
    setSelectedServices((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              services: cat.services.map((service) =>
                service.id === serviceId
                  ? {
                      ...service,
                      employees: updateOrInsert(service.employees || [], employeeId, {
                        active: true,
                        commission,
                      }),
                    }
                  : service
              ),
            }
          : cat
      )
    );
  };

  const updateOrInsert = (list, employeeId, data) => {
    const exists = list.find((e) => e.id === employeeId);
    if (exists) {
      return list.map((e) => (e.id === employeeId ? { ...e, ...data } : e));
    }
    return [...list, { id: employeeId, ...data }];
  };

  const getServiceValue = (categoryId, serviceId, field) => {
    const cat = selectedServices.find((c) => c.id === categoryId);
    const service = cat?.services.find((s) => s.id === serviceId);
    return service?.[field];
  };

  const updateServiceDetail = (categoryId, serviceId, field, value) => {
    setSelectedServices((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              services: cat.services.map((service) =>
                service.id === serviceId ? { ...service, [field]: value } : service
              ),
            }
          : cat
      )
    );
  };

  const formatDurationLabel = (minutes) => {
    if (!minutes || minutes <= 0) return null;

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    if (h && m) return `${h}h ${m}min`;
    if (h) return `${h}h`;
    if (m) return `${m}min`;
  };

  return (
    <>
    <ReportHeader name="Serviços Sugeridos" />
      <div className="container suggestion-service">
        <MenuTitle title="Serviços Sugeridos" showDivider />

        <Container className="suggestion-service__container">
          <div className="suggestion-service__intro">
            <FontAwesomeIcon icon={faLightbulb} className="suggestion-service__intro-icon" />
            <p className="suggestion-service__intro-text">
              Adicione rapidamente categorias e serviços populares ao seu salão. Configure duração,
              valor e profissionais para cada serviço.
            </p>
          </div>

          {isLoading ? (
            <AppLoading />
          ) : suggestions.length === 0 ? (
            <EmptyState
              title="Nenhuma sugestão disponível"
              description="No momento não há sugestões de serviços. Tente novamente mais tarde."
              icon={<FontAwesomeIcon icon={faLightbulb} />}
            />
          ) : (
            <Accordion defaultActiveKey="0" className="suggestion-service__accordion">
              {suggestions.map((category, idx) => (
                <Accordion.Item eventKey={idx.toString()} key={category.id}>
                  <Accordion.Header>
                    <span className="suggestion-service__category-name">{category.name}</span>
                    {getSelectedCount(category.id) > 0 && (
                      <span className="suggestion-service__badge">
                        {getSelectedCount(category.id)}
                      </span>
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="suggestion-service__services">
                      {category.services.map((service) => (
                        <Card
                          className={`suggestion-service__card ${
                            isSelected(category.id, service.id) ? 'is-selected' : ''
                          }`}
                          key={service.id}
                        >
                          <Card.Body>
                            <div className="suggestion-service__card-header">
                              <h6 className="suggestion-service__service-name">{service.name}</h6>
                              <AppButton
                                size="sm"
                                variant={
                                  isSelected(category.id, service.id) ? 'success' : 'outline'
                                }
                                name={isSelected(category.id, service.id) ? 'Adicionado' : 'Adicionar'}
                                onClick={() => toggleService(category.id, service.id)}
                              />
                            </div>

                            {isSelected(category.id, service.id) && (
                              <div className="suggestion-service__details">
                                {/* Duração e Valor */}
                                <div className="suggestion-service__row">
                                  <div className="suggestion-service__field">
                                    <label className="suggestion-service__label">
                                      Duração (min) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      placeholder="Ex: 30"
                                      min={1}
                                      value={
                                        getServiceValue(category.id, service.id, 'duration') || ''
                                      }
                                      onChange={(e) =>
                                        updateServiceDetail(
                                          category.id,
                                          service.id,
                                          'duration',
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                    {getServiceValue(category.id, service.id, 'duration') > 0 && (
                                      <small className="suggestion-service__hint">
                                        {formatDurationLabel(
                                          getServiceValue(category.id, service.id, 'duration')
                                        )}
                                      </small>
                                    )}
                                  </div>

                                  <div className="suggestion-service__field">
                                    <label className="suggestion-service__label">
                                      Valor (R$) <span className="text-danger">*</span>
                                    </label>
                                    <IntlCurrencyInput
                                      currency="BRL"
                                      config={currencyConfig}
                                      className="form-control"
                                      value={getServiceValue(category.id, service.id, 'value') || 0}
                                      onChange={(event, rawValue) =>
                                        updateServiceDetail(
                                          category.id,
                                          service.id,
                                          'value',
                                          rawValue
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                {/* Profissionais */}
                                {employees.length > 0 && (
                                  <div className="suggestion-service__employees">
                                    <label className="suggestion-service__label suggestion-service__label--section">
                                      Profissionais que realizam este serviço
                                    </label>
                                    <div className="suggestion-service__employees-grid">
                                      {employees.map((emp) => {
                                        const selectedEmp = getEmployeeSelection(
                                          category.id,
                                          service.id,
                                          emp.id
                                        );
                                        const isActive = selectedEmp?.active || false;

                                        return (
                                          <div
                                            key={emp.id}
                                            className={`suggestion-service__employee-card ${
                                              isActive ? 'is-active' : ''
                                            }`}
                                            onClick={() =>
                                              toggleEmployeeFlag(
                                                category.id,
                                                service.id,
                                                emp.id,
                                                !isActive
                                              )
                                            }
                                          >
                                            <div className="suggestion-service__employee-info">
                                              <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  toggleEmployeeFlag(
                                                    category.id,
                                                    service.id,
                                                    emp.id,
                                                    e.target.checked
                                                  );
                                                }}
                                                className="suggestion-service__employee-checkbox"
                                              />
                                              <span className="suggestion-service__employee-name">
                                                {displayName(emp.name) || emp.email}
                                              </span>
                                            </div>

                                            {isActive && (
                                              <div
                                                className="suggestion-service__commission"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <input
                                                  type="number"
                                                  className="form-control form-control-sm"
                                                  placeholder="% Comissão"
                                                  min={0}
                                                  max={100}
                                                  value={selectedEmp.commission || ''}
                                                  onChange={(e) =>
                                                    updateEmployeeCommission(
                                                      category.id,
                                                      service.id,
                                                      emp.id,
                                                      Number(e.target.value)
                                                    )
                                                  }
                                                />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Container>

        {/* Botão fixo */}
        {getTotalSelectedCount() > 0 && (
          <div className="suggestion-service__footer">
            <AppContainer>
              <AppButton
                name={`Finalizar (${getTotalSelectedCount()} ${
                  getTotalSelectedCount() === 1 ? 'serviço' : 'serviços'
                })`}
                width="100%"
                onClick={saveAll}
                loading={isSaving}
                disabled={isSaving}
              />
            </AppContainer>
          </div>
        )}
      </div>
    </>
  );
}

export default SuggestionService;